"use client"

import { useEffect } from "react"

/**
 * Hook to manage body scroll locking and ensure proper cleanup.
 * This fixes the issue where modals leave `pointer-events: none` on the body.
 */
export function useBodyScrollLock(isOpen: boolean) {
  useEffect(() => {
    if (!isOpen) {
      // Force cleanup when modal is closed
      const cleanup = () => {
        document.body.style.pointerEvents = ""
        document.body.style.overflow = ""
        document.body.removeAttribute("data-scroll-locked")

        // Also remove any Radix-specific attributes that might be causing issues
        document.body.removeAttribute("data-radix-prevent-auto-focus")
        document.body.removeAttribute("data-radix-focus-guard")
      }

      // Immediate cleanup
      cleanup()

      // Delayed cleanup to ensure it runs after any animations
      const timeoutId = setTimeout(cleanup, 200)

      return () => clearTimeout(timeoutId)
    }
  }, [isOpen])

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      const cleanup = () => {
        document.body.style.pointerEvents = ""
        document.body.style.overflow = ""
        document.body.removeAttribute("data-scroll-locked")
        document.body.removeAttribute("data-radix-prevent-auto-focus")
        document.body.removeAttribute("data-radix-focus-guard")
      }

      // Immediate cleanup
      cleanup()

      // Delayed cleanup to be extra sure
      setTimeout(cleanup, 100)
    }
  }, [])

  // Global event listener for any modal-related DOM mutations
  useEffect(() => {
    if (typeof window === "undefined") return

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "attributes" && mutation.target === document.body) {
          const style = (document.body as HTMLElement).style

          // If modal is closed but pointer-events is still "none", fix it
          if (!isOpen && style.pointerEvents === "none") {
            setTimeout(() => {
              document.body.style.pointerEvents = ""
              document.body.style.overflow = ""
            }, 50)
          }
        }
      })
    })

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["style", "data-scroll-locked", "data-radix-prevent-auto-focus"],
    })

    return () => observer.disconnect()
  }, [isOpen])
}
